import html2canvas from "html2canvas";
import React from "react";
import { useRegDetail } from "shared/context/regDetail";
import { getResolvedString } from "shared/utils";
import { SocialMediaPlatform } from "shared/utils/enums";
import { useGetImageBase64, useGetTemplates, useUploadTemplate } from "./dataloader";
import { UseSocialMediaShareInput } from "./types";
import { getSocialMediaShareUrl, getSortedTemplateList, SocialMediaUrl } from "./utils";
import { useSearchParams } from "react-router-dom";
import QueryParamKey from "../../enums/QueryParamKey";
import { ApiSocialPostTemplate } from "apps/console/modules/registration/configurations/socialMedia/list/types";
import { AnalyticsActionNameEnum, AnalyticsEventNameEnum, AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";

export default function useSocialMediaShareService({ logEvent, ...input }: UseSocialMediaShareInput) {
  const [searchParams] = useSearchParams();
  const { id: regId, key, eventSeriesName, clientName } = useRegDetail();
  const [templateBlob, setTemplateBlob] = React.useState<any>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const { mutateAsync: uploadPost } = useUploadTemplate();
  const templateRef = React.useRef<any>();
  const newSocialPostWindowRef = React.useRef<Window | null>();
  const [selectedTemplate, setSelectedTemplate] = React.useState<ApiSocialPostTemplate>();
  const [templateBgBase64, setTemplateBgBase64] = React.useState<string>();
  const { headshot, regParticipantId, listApi } = input;
  const successHandler = React.useCallback(
    (data: Array<ApiSocialPostTemplate>) => {
      if (selectedTemplate) return;
      if (data && data.length > 0) {
        setSelectedTemplate(getSortedTemplateList(data)[0]);
      }
    },
    [selectedTemplate]
  );

  const { data: headshotBase64 } = useGetImageBase64(headshot, true);
  useGetImageBase64(selectedTemplate?.templateLocation, Boolean(true), (base64: string) => {
    setTemplateBgBase64(base64);
  });

  const { data: templateData } = useGetTemplates({
    registrationId: regId,
    registrationParticipantId: regParticipantId,
    successHandler,
    listApi
  });

  const templateList = React.useMemo(() => getSortedTemplateList(templateData), [templateData]);

  React.useEffect(() => {
    if (selectedTemplate) {
      return;
    }
    const tempId = searchParams.get(QueryParamKey.SOCIAL_MEDIA_TEMPLATE_ID);
    if (tempId) {
      const tempObj = templateList?.find(i => i.id === tempId);
      setSelectedTemplate(tempObj);
    }
  }, [templateList, searchParams, selectedTemplate]);

  const headShotPlacement = React.useMemo(() => {
    const headShotProperties = selectedTemplate?.properties?.userImage;
    return {
      top: headShotProperties?.position.y,
      left: headShotProperties?.position.x,
      height: headShotProperties?.position.h,
      width: headShotProperties?.position.w
    };
  }, [selectedTemplate]);

  const userDetailsPlacement = React.useMemo(() => {
    const userDetailsProperties = selectedTemplate?.properties?.userDetails;
    return {
      top: userDetailsProperties?.position.y,
      left: userDetailsProperties?.position.x,
      height: userDetailsProperties?.position.h,
      width: userDetailsProperties?.position.w
    };
  }, [selectedTemplate]);

  const onTemplateChange = React.useCallback((t: ApiSocialPostTemplate) => {
    setTemplateBgBase64(undefined);
    setSelectedTemplate(t);
  }, []);

  const closeSocialPostNewWindowOnError = React.useCallback(() => {
    if (newSocialPostWindowRef.current) {
      try {
        newSocialPostWindowRef.current.close();
      } catch (e) {
      } finally {
        newSocialPostWindowRef.current = null;
      }
    }
  }, []);

  const sharePost = React.useCallback(
    (platform: SocialMediaPlatform, shareText?: string) => {
      const timestamp = new Date().getTime().toString();
      const socialShareUrl = getSocialMediaShareUrl(regParticipantId, platform, timestamp);
      const urlToShare = getResolvedString(socialShareUrl, {
        regParticipantId,
        platform,
        timestamp: new Date().getTime()
      });
      let socialMediaSpecificUrl = getResolvedString(SocialMediaUrl[platform], {
        urlToShare
      });
      if (shareText) {
        socialMediaSpecificUrl = `${socialMediaSpecificUrl}&text=${shareText}`;
      }
      if (newSocialPostWindowRef.current) {
        newSocialPostWindowRef.current.location.href = socialMediaSpecificUrl;
        newSocialPostWindowRef.current = null;
      } else {
        const windowInstance = window.open(socialMediaSpecificUrl, "_blank");
        if (!windowInstance) {
          // If there new window open is blocked
          const link = document.createElement("a");
          link.href = socialMediaSpecificUrl;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    },
    [regParticipantId]
  );

  const handleUpload = React.useCallback(
    async (platform: SocialMediaPlatform, shareText?: string) => {
      logEvent?.(
        AnalyticsEventNameEnum.CLICK,
        {
          screenType: AnalyticsScreenNameEnum.POST_ON_SOCIAL_MEDIA,
          action:
            platform === SocialMediaPlatform.LINKEDIN
              ? AnalyticsActionNameEnum.SHARE_ON_LINKEDIN
              : platform === SocialMediaPlatform.X
              ? AnalyticsActionNameEnum.SHARE_ON_TWITTER
              : undefined,
          contextEntityId: selectedTemplate?.id
        },
        AnalyticsDatastoreType.MIXPANEL_INTERNAL
      );
      if (templateBlob?.id === selectedTemplate?.id) {
        sharePost(platform, shareText);
        return;
      }
      const template: HTMLElement | null = document.querySelector("#template");
      if (!template) return;
      await setLoading(true);

      html2canvas(template)
        .then(canvas => {
          canvas.toBlob(blob => {
            if (blob) {
              const data = new FormData();
              const socialPostData = {
                registrationParticipantId: regParticipantId,
                registrationKey: key, //pending: data should be in reg details api
                clientName: clientName,
                eventSeriesName: eventSeriesName
              };
              const socialPostParticipantTemplate = new Blob([JSON.stringify(socialPostData)], {
                type: "application/json"
              }); //because we need to specify content type specifically
              data.append("file", blob);
              data.append("socialPostParticipantTemplate", socialPostParticipantTemplate);
              setLoading(false);
              uploadPost(data)
                .then(() => {
                  setTemplateBlob({ id: selectedTemplate?.id, blob });
                  sharePost(platform, shareText);
                })
                .catch(() => {
                  closeSocialPostNewWindowOnError();
                });
            }
          }, "image/jpeg");
        })
        .catch(e => {
          closeSocialPostNewWindowOnError();
        })
        .finally(() => setLoading(false));
    },
    [
      clientName,
      closeSocialPostNewWindowOnError,
      eventSeriesName,
      key,
      regParticipantId,
      selectedTemplate?.id,
      sharePost,
      templateBlob?.id,
      uploadPost,
      logEvent
    ]
  );

  const clickLinkFromBlob = React.useCallback(
    blob => {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${selectedTemplate?.name}.jpeg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setLoading(false);
    },
    [selectedTemplate]
  );

  const handleDownload = React.useCallback(() => {
    logEvent?.(
      AnalyticsEventNameEnum.CLICK,
      {
        screenType: AnalyticsScreenNameEnum.POST_ON_SOCIAL_MEDIA,
        action: AnalyticsActionNameEnum.DOWNLOAD,
        contextEntityId: selectedTemplate?.id
      },
      AnalyticsDatastoreType.MIXPANEL_INTERNAL
    );
    if (templateBlob) {
      clickLinkFromBlob(templateBlob.blob);
      return;
    }
    const template: HTMLElement | null = document.querySelector("#template");
    template &&
      html2canvas(template).then(canvas => {
        canvas.toBlob(blob => {
          clickLinkFromBlob(blob);
        }, "image/jpeg");
      });
  }, [clickLinkFromBlob, templateBlob, logEvent, selectedTemplate?.id]);

  return {
    headShotPlacement,
    userDetailsPlacement,
    templateList,
    selectedTemplate,
    setSelectedTemplate,
    participantDetails: input.participantData,
    handleUpload,
    templateRef,
    loading: loading,
    onTemplateChange,
    headshotBase64,
    handleDownload,
    templateBgBase64
  };
}
